import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Gift, Check, TrendingUp, Users, Plus, Clock,
  ChevronRight, Loader2, QrCode, Store
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import PartnerLayout from '../../components/partner/PartnerLayout';

const PartnerDashboardPage = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [benefits, setBenefits] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [stats, setStats] = useState({
    total_benefits: 0,
    approved_benefits: 0,
    pending_benefits: 0,
    total_redemptions: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('partner_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const [partnerRes, benefitsRes, redemptionsRes] = await Promise.all([
        api.get('/partners/me', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/partners/me/benefits', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/partners/redemptions', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setPartner(partnerRes.data);
      setBenefits(benefitsRes.data || []);
      setRedemptions(redemptionsRes.data?.redemptions || []);
      
      // Calculate stats
      const benefitsList = benefitsRes.data || [];
      setStats({
        total_benefits: benefitsList.length,
        approved_benefits: benefitsList.filter(b => b.status === 'approved').length,
        pending_benefits: benefitsList.filter(b => b.status === 'pending').length,
        total_redemptions: redemptionsRes.data?.total || 0
      });
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('partner_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PartnerLayout currentPage="dashboard">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout currentPage="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-black text-white">
            ¡Hola, {partner?.business_name}!
          </h1>
          <p className="text-zinc-400">Bienvenido al Portal de Partners BRAVX</p>
        </div>
        <Button
          onClick={() => navigate('/partner/crear-beneficio')}
          className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
          data-testid="create-benefit-btn"
        >
          <Plus size={18} className="mr-2" />
          Nuevo Beneficio
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6 lg:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
              <Gift className="text-[#D4AF37]" size={20} />
            </div>
            <span className="text-zinc-400 text-sm">Beneficios</span>
          </div>
          <p className="text-2xl lg:text-3xl font-black text-white">{stats.total_benefits}</p>
          <p className="text-xs text-zinc-500 mt-1">{stats.approved_benefits} activos</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
              <Check className="text-green-500" size={20} />
            </div>
            <span className="text-zinc-400 text-sm">Canjes</span>
          </div>
          <p className="text-2xl lg:text-3xl font-black text-white">{stats.total_redemptions}</p>
          <p className="text-xs text-zinc-500 mt-1">códigos validados</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <Clock className="text-yellow-500" size={20} />
            </div>
            <span className="text-zinc-400 text-sm">Pendientes</span>
          </div>
          <p className="text-2xl lg:text-3xl font-black text-white">{stats.pending_benefits}</p>
          <p className="text-xs text-zinc-500 mt-1">en revisión</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-purple-500" size={20} />
            </div>
            <span className="text-zinc-400 text-sm">Aprobados</span>
          </div>
          <p className="text-2xl lg:text-3xl font-black text-white">{stats.approved_benefits}</p>
          <p className="text-xs text-zinc-500 mt-1">visibles en BRAVX</p>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-4 mb-6 lg:mb-8">
        {/* Verify Code CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-[#D4AF37]/20 to-zinc-900 border border-[#D4AF37]/30 rounded-xl p-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-xl flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
              <QrCode size={28} className="text-black" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-white text-lg mb-1">Verificar Código</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Valida los códigos BRAVX de tus clientes para aplicar descuentos
              </p>
              <Button
                onClick={() => navigate('/partner/verificar')}
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                data-testid="verify-code-cta-btn"
              >
                Ir a Verificar
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Create Benefit CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500/30 to-purple-600/10 rounded-xl flex items-center justify-center">
              <Gift size={28} className="text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-white text-lg mb-1">Crear Beneficio</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Agrega nuevos beneficios para los suscriptores de FULL BRAVX
              </p>
              <Button
                onClick={() => navigate('/partner/crear-beneficio')}
                variant="outline"
                className="border-zinc-700 text-white hover:bg-zinc-800"
                data-testid="create-benefit-cta-btn"
              >
                Crear Beneficio
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Recent Redemptions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white">Últimos Canjes</h2>
          <Link 
            to="/partner/verificar" 
            className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
          >
            Ver todos <ChevronRight size={14} />
          </Link>
        </div>

        {redemptions.length === 0 ? (
          <div className="text-center py-12">
            <Check size={48} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="text-white font-medium mb-2">Sin canjes recientes</h3>
            <p className="text-zinc-500 text-sm">
              Los códigos validados aparecerán aquí
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {redemptions.slice(0, 5).map((redemption, index) => (
              <motion.div
                key={redemption.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg"
              >
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <Check size={18} className="text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{redemption.benefit_title}</p>
                  <p className="text-zinc-500 text-sm">{redemption.user_name}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-zinc-600 text-xs font-mono">{redemption.code}</p>
                  <p className="text-zinc-500 text-xs">
                    {new Date(redemption.created_at).toLocaleDateString('es-AR')}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </PartnerLayout>
  );
};

export default PartnerDashboardPage;
